import { useGoogleReCaptcha } from '@/providers/GoogleReCaptchaProvider'
import { useCallback, useEffect, useRef } from 'react'

interface UseRecaptchaProps {
  requiresHumanCaptcha?: boolean
  humanCaptchaSiteKey?: string | null
  onHumanCaptchaComplete?: (token: string) => Promise<void>
}

export function useRecaptcha(props?: UseRecaptchaProps) {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const onCompleteRef = useRef(props?.onHumanCaptchaComplete)
  onCompleteRef.current = props?.onHumanCaptchaComplete

  const RECAPTCHA_TIMEOUT_MS = 15_000

  const getToken = useCallback(async (action: string): Promise<string | null> => {
    if (!executeRecaptcha) return null
    try {
      const tokenPromise = executeRecaptcha(action).catch(() => null)
      const timeoutPromise = new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), RECAPTCHA_TIMEOUT_MS)
      )
      return (await Promise.race([tokenPromise, timeoutPromise])) ?? null
    } catch (err) {
      console.error('executeRecaptcha error', err)
      return null
    }
  }, [executeRecaptcha])

  const handleHumanCaptchaComplete = useCallback(async (token: string) => {
    await onCompleteRef.current?.(token)
  }, [])

  const resetRecaptcha = useCallback((setCaptchaError?: (error: string | null) => void) => {
    if (setCaptchaError) {
      setCaptchaError(null)
    }
    // Only remove the v2 HumanCaptcha script we add - never the v3 script.
    // v3 URL: api.js?render=KEY, v2 URL: api.js (no render param)
    const v2Scripts = document.querySelectorAll('script[src*="recaptcha/api.js"]')
    v2Scripts.forEach((script) => {
      if (script.getAttribute('src') && !script.getAttribute('src')!.includes('render=')) {
        script.remove()
      }
    })
  }, [])

  const handleBackFromHumanCaptcha = useCallback((
    setRequiresHumanCaptcha: (value: boolean) => void,
    setHumanCaptchaSiteKey: (value: string | null) => void,
    setCaptchaError: (value: string | null) => void,
    setHumanCaptchaToken?: (value: string | null) => void,
  ) => {
    setRequiresHumanCaptcha(false)
    setHumanCaptchaSiteKey(null)
    setCaptchaError(null)
    if (setHumanCaptchaToken) {
      setHumanCaptchaToken(null)
    }
  }, [])

  // Load human reCAPTCHA v2 script when needed (v2 = checkbox, v3 = invisible from provider)
  useEffect(() => {
    if (props?.requiresHumanCaptcha && props?.humanCaptchaSiteKey) {
      (window as any).handleHumanCaptchaComplete = handleHumanCaptchaComplete

      const v2Script = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]')
      if (!v2Script) {
        const script = document.createElement('script')
        script.src = 'https://www.google.com/recaptcha/api.js'
        script.async = true
        script.defer = true
        script.onload = () => {
          window.dispatchEvent(new CustomEvent('recaptchaReady'))
        }
        document.head.appendChild(script)
      } else {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('recaptchaReady'))
        }, 100)
      }
    }
  }, [props?.requiresHumanCaptcha, props?.humanCaptchaSiteKey])

  // Cleanup callback on unmount
  useEffect(() => {
    return () => {
      delete (window as any).handleHumanCaptchaComplete
    }
  }, [])

  return {
    getToken,
    handleHumanCaptchaComplete,
    resetRecaptcha,
    handleBackFromHumanCaptcha,
  }
}
